import React from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {controlClass, fieldError, primaryButtonClass, Spinner} from '../formStyles';
import {CheckIcon} from '../Icons';
import {PRO_FEATURES, TRIAL_DAYS, TRIAL_PRICE_USD, yearlySavings} from '../../domain/plans';
import type {BillingCycleName, PaddleProducts} from '../../domain/types';

export interface SignupStartValues {
    email: string;
    name: string;
    billingCycle: BillingCycleName;
}

const signupSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  // The server treats the name as optional, but it is what the login-link email greets them by, so
  // the form asks for it properly rather than sending an empty string.
  name: Yup.string()
    .min(2, 'Please enter your name')
    .required('Name is required'),
});

interface SignupStartFormProps {
    products: PaddleProducts;
    initialEmail: string;
    initialName: string;
    initialBillingCycle: BillingCycleName;
    submitting: boolean;
    onSubmit: (values: SignupStartValues) => Promise<void>;
}

/**
 * Email, name and which plan the trial starts on. **There is no password field** — the account does
 * not exist yet, so there is nowhere to put one; it is chosen after the login link is used.
 */
const SignupStartForm: React.FC<SignupStartFormProps> = ({
  products,
  initialEmail,
  initialName,
  initialBillingCycle,
  submitting,
  onSubmit,
}) => (
  <Formik
    initialValues={{email: initialEmail, name: initialName, billingCycle: initialBillingCycle}}
    validationSchema={signupSchema}
    onSubmit={async (values, {setSubmitting}) => {
      await onSubmit({...values, email: values.email.trim(), name: values.name.trim()});
      setSubmitting(false);
    }}
    validateOnChange={false}
    validateOnBlur={false}
  >
    {({values, setFieldValue, isSubmitting}) => {
      const busy = isSubmitting || submitting;
      const savings = yearlySavings(products.monthly.price, products.yearly.price);

      return (
        <Form className="mt-5 space-y-3.5">
          <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/[0.08] px-3 py-2.5 text-xs text-indigo-200">
            <span className="font-semibold">${TRIAL_PRICE_USD} today for a {TRIAL_DAYS}-day trial.</span>{' '}
            Renews automatically at the plan price below unless you cancel first.
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="name">
                            Name
            </label>
            <Field
              id="name"
              type="text"
              name="name"
              autoComplete="name"
              autoFocus
              className={controlClass}
              placeholder="Jane Doe"
            />
            <ErrorMessage name="name">{fieldError}</ErrorMessage>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="email">
                            Email
            </label>
            <Field
              id="email"
              type="text"
              name="email"
              autoComplete="email"
              className={controlClass}
              placeholder="you@example.com"
            />
            <ErrorMessage name="email">{fieldError}</ErrorMessage>
          </div>

          <fieldset>
            <legend className="mb-1 block text-xs font-medium text-gray-300">Billing</legend>
            <div className="grid grid-cols-2 gap-2">
              {(['monthly', 'yearly'] as const).map((cycle) => {
                const product = products[cycle];
                const selected = values.billingCycle === cycle;

                return (
                  <button
                    key={cycle}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setFieldValue('billingCycle', cycle)}
                    className={`rounded-lg border px-3 py-2.5 text-left transition-colors cursor-pointer ${
                      selected
                        ? 'border-indigo-500 bg-indigo-500/[0.08]'
                        : 'border-gray-800 bg-gray-950/60 hover:border-gray-700'
                    }`}
                  >
                    <span className="block text-xs font-medium text-gray-200">
                      {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                    </span>
                    <span className="mt-0.5 block text-sm tabular-nums text-gray-100">
                                            then ${product.price}
                      <span className="text-xs text-gray-500">
                        {cycle === 'monthly' ? '/month' : '/year'}
                      </span>
                    </span>
                    {cycle === 'yearly' && savings > 0 && (
                      <span className="mt-0.5 block text-xs text-emerald-300">Save ${savings}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <ul className="space-y-1.5 rounded-lg border border-gray-800 bg-gray-950/40 px-3 py-2.5">
            {PRO_FEATURES.map(feature => (
              <li key={feature} className="flex items-start gap-2 text-xs text-gray-400">
                <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <button type="submit" disabled={busy} className={`w-full ${primaryButtonClass}`}>
            {busy && <Spinner />}
            {busy ? 'Opening checkout…' : `Start trial — $${TRIAL_PRICE_USD} today`}
          </button>

          <p className="text-xs text-gray-600">
                        Your card details go straight to Paddle, our merchant of record. You’ll be charged
                        ${TRIAL_PRICE_USD} today; after {TRIAL_DAYS} days it renews automatically at the plan
                        price above unless you cancel — Paddle confirms the exact renewal date before you pay,
                        and your account is created once checkout completes.
          </p>
        </Form>
      );
    }}
  </Formik>
);

export default SignupStartForm;
